/* ReviewerView — strictly-scoped Reviewer-only surface.
 * Renders ONLY V.I-visible records: own audit rows, own accepted
 * invitation, own suspension. No commercial / operator / cross-
 * member content. No action buttons (Reviewer is read-only in 0.7).
 */
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AuditFeed } from './AuditFeed';

export function ReviewerView({ orgName, myUserId, members, invitations, suspensions, audit }) {
  // S6 RLS filters server-side. We additionally pre-filter client-
  // side as a defence-in-depth (NOT a security boundary) so the UI
  // never shows anything outside V.I scope even if the server were
  // mis-configured.
  const myMembership = members.find((m) => m.user_id === myUserId);
  const myInvitation = invitations.find((i) => i.accepted_by === myUserId);
  const mySuspensions = suspensions.filter((s) => s.member_user_id === myUserId);

  return (
    <div className="mx-auto w-full max-w-4xl p-4 sm:p-6" data-testid="emp-reviewer-view">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-wider text-slate-300"
          data-testid="emp-reviewer-org-label">{orgName}</p>
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl"
          data-testid="emp-reviewer-title">My membership</h1>
        <p className="mt-1 text-sm text-slate-300">
          You have <Badge variant="outline" data-testid="emp-reviewer-my-role">reviewer</Badge> access.
          You can see only records about your own membership.
        </p>
      </header>

      <section className="mb-6">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-300">
          Membership
        </h2>
        {myMembership ? (
          <Card className="p-4" data-testid="emp-reviewer-membership">
            <p className="font-mono text-xs text-slate-300">{myMembership.user_id}</p>
            <p className="mt-1">role: <Badge variant="outline">{myMembership.role}</Badge></p>
            <p className="mt-1 text-xs text-slate-300">
              joined {myMembership.joined_at ? new Date(myMembership.joined_at).toLocaleDateString() : '—'}
            </p>
          </Card>
        ) : (
          <Card className="p-4 text-sm text-slate-300" data-testid="emp-reviewer-no-membership">
            No membership record visible.
          </Card>
        )}
      </section>

      <section className="mb-6">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-300">
          My invitation
        </h2>
        {myInvitation ? (
          <Card className="p-4" data-testid="emp-reviewer-invitation">
            <p>{myInvitation.target_email}</p>
            <p className="mt-1">
              <Badge variant="secondary">{myInvitation.status}</Badge>
              {' '}as <Badge variant="outline">{myInvitation.target_role}</Badge>
            </p>
          </Card>
        ) : (
          <Card className="p-4 text-sm text-slate-300" data-testid="emp-reviewer-no-invitation">
            No accepted invitation record visible.
          </Card>
        )}
      </section>

      <section className="mb-6">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-300">
          My suspensions
        </h2>
        {mySuspensions.length ? (
          <div className="space-y-2" data-testid="emp-reviewer-suspensions">
            {mySuspensions.map((s) => (
              <Card key={s.id} className="p-3" data-testid={`emp-reviewer-suspension-${s.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={s.status === 'active' ? 'destructive' : 'outline'}>
                    {s.status}
                  </Badge>
                  {s.is_emergency && <Badge variant="destructive">emergency</Badge>}
                  <span className="text-xs text-slate-300">
                    {s.suspended_at ? new Date(s.suspended_at).toLocaleDateString() : ''}
                    {s.expires_at  ? ` → ${new Date(s.expires_at).toLocaleDateString()}` : ''}
                  </span>
                </div>
                <p className="mt-2 text-sm">{s.reason || '—'}</p>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-4 text-sm text-slate-300" data-testid="emp-reviewer-no-suspensions">
            No suspension records visible.
          </Card>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-300">
          My audit trail
        </h2>
        <AuditFeed audit={audit} />
      </section>
    </div>
  );
}
